package com.macrofocus.eflux.components

import com.macrofocus.common.properties.Property
import com.macrofocus.eflux.data.quest.loadBusDataFrames
import com.macrofocus.eflux.network.DerivedBusColumn
import com.macrofocus.eflux.network.Node
import com.macrofocus.eflux.network.NodeField
import com.macrofocus.eflux.network.physical.Bus
import com.macrofocus.eflux.network.toFixed
import com.macrofocus.eflux.toRawString
import com.macrofocus.eflux.ui.Mark
import com.macrofocus.eflux.ui.PaletteType
import com.macrofocus.eflux.ui.Sparkline
import com.macrofocus.eflux.ui.data.SubstationChip
import emotion.react.css
import i18next.invoke
import i18next.useTranslation
import js.array.ReadonlyArray
import js.objects.unsafeJso
import mui.material.TooltipPlacement
import mui.material.Typography
import mui.material.TypographyAlign
import mui.material.styles.Theme
import mui.material.styles.TypographyVariant
import mui.material.styles.useTheme
import mui.system.Box
import mui.system.sx
import org.mkui.react.useProperty
import org.molap.aggregates.cube.DistributiveStatistics
import org.molap.dataframe.DataFrame
import org.molap.dataframe.rowmajor.RowMajorDataFrame
import org.molap.series.Series
import react.*
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import web.cssom.*

external interface NodeInfoProps : PropsWithChildren {
    var primary: Boolean
    var node: Node?
    var tooltipNodes: Property<Array<NodeField<*>>>
    var masterDateTime: String?
}

private operator fun Double?.times(other: Double?) = if (this != null && other != null) this * other else null
private operator fun Double?.div(other: Double?) = if (this != null && other != null) this / other else null
private operator fun Double?.plus(other: Double?) = if (this != null && other != null) this + other else null

val NodeInfo = FC<NodeInfoProps> { props ->
    val theme = useTheme<Theme>()
    val context = useRequired(EFluxContext)
    val (t, _) = useTranslation()

    val node = props.node
    val (fullName, _) = useProperty(context.dataLoader.fullName)

    val (displayDateTime, _) = useProperty(context.configuration.displayDateTime)
    val (maxDateTime, _) = useProperty(context.configuration.maxDateTime)
    val (lagUnit, _) = useProperty(context.configuration.lagUnit)
    val paletteType = if (theme.asDynamic().palette.mode == "dark") PaletteType.dark else PaletteType.light

    val (getBusDataFrame, setBusDataFrame) = useState(null as DataFrame<Bus, RowMajorDataFrame.Column<Bus, Any?>, Any?>?)
    useEffect(node, maxDateTime.toRawString()) {
        val busId = node?.bus?.primary?.id
        if (busId != null) {
            val date = maxDateTime.toRawString()

            val busDataFrame = loadBusDataFrames(busId, date, lagUnit)

            setBusDataFrame(busDataFrame)
        } else {
            setBusDataFrame(null)
        }
    }

    if (node != null) {
        val v = "${node.bus?.primary?.v?.amount?.toFixed(0)} ${node.bus?.primary?.v?.unit}"

        Box {
            sx {
                display = Display.flex
                justifyContent = JustifyContent.center // centers horizontally
                // alignItems = "center" // use if you also want vertical centering
                width = "1.0".unsafeCast<Width>() // takes full width of parent
                maxWidth = 280.px
                overflowY = Overflow.clip
            }
            SubstationChip {
//            this.probing = if(props.primary) context.dataLoader.controller.secondaryLogicalBusProbing else context.dataLoader.controller.logicalBusProbing
//            this.selection = if(props.primary) context.dataLoader.controller.secondaryLogicalBusSelection else context.dataLoader.controller.logicalBusSelection
                this.dataLoader = context.dataLoader
                this.logicalBusId = node.id
                this.placement = TooltipPlacement.bottom
            }
        }
//        Tooltip {
//            title = if (!fullName) ReactNode(node.fullName) else ReactNode(node.id.name)
//            placement = TooltipPlacement.bottom
//
//            Typography {
//                align = TypographyAlign.center
//                variant = TypographyVariant.subtitle2
//                +if(fullName) node.fullName else node.id.name
//            }
//        }

        Typography {
            align = TypographyAlign.left
            variant = TypographyVariant.body1
            ariaLabel = "$v @ ${props.masterDateTime}"
            if(node.bus?.primary?.v?.amount != null) {
                +v
            } else {
                +""
            }
        }

        Typography {
            sx {
                lineHeight = 1.rem
                display = Display.inlineBlock
                marginBottom = 8.px
                verticalAlign = VerticalAlign.top
            }

            align = TypographyAlign.left
            variant = TypographyVariant.caption
//                        asDynamic().display = Display.inline
//                    component = span

            +t("variable.node.v.label")
        }

        val dataFrame = getBusDataFrame
        val getWidth = 180.0
        val maximum = node.bus?.primary?.record?.baskv?.amount * node.bus?.primary?.record?.evhi?.amount
        val minimum = node.bus?.primary?.record?.baskv?.amount * node.bus?.primary?.record?.evlo?.amount

        Box {
            sx {
                display = Display.inlineFlex
                marginLeft = 10.px
            }

            if (dataFrame != null) {
                mui.material.Tooltip {
                    title = ReactNode(t("variable.node.v.label"))

                    val valueMarks: ReadonlyArray<Mark> = arrayOf(
                        unsafeJso { value = 0; label = "${minimum?.toFixed(0)}" },
                        unsafeJso { value = 0.5; label = "${((minimum + maximum) / 2.0)?.toFixed(0)}" },
                        unsafeJso { value = 1; label = "${maximum?.toFixed(0)}" },
                    )

                    div {
                        css {
                            position = Position.relative
                            height = 100.pct
                            width = 26.px
                        }
                        for (b in valueMarks) {
                            span {
                                css {
                                    position = Position.absolute
                                    fontSize = 0.875.rem
                                    bottom = ((b.value!!.toDouble() * 100)).pct
                                    transform = "translateY(50%);".unsafeCast<Transform>()
                                }
                                +b.label!!
                            }
                        }
                    }
                }
            }

            val column = DerivedBusColumn.v
            val series = dataFrame?.getColumn(column) as Series<Any?, *>?
            val dateTime = dataFrame?.getColumn(DerivedBusColumn.datetime) as Series<Any?, *>?
            val statistics = if (series != null) DistributiveStatistics(series) else null
            val dateTimeStatistics = if(dateTime != null) DistributiveStatistics(dateTime) else null

            Sparkline {
                this.series = series
                this.maximum = maximum
                this.minimum = minimum
                this.getWidth = getWidth
                this.dateTime = dateTime
                this.dateTimeStatistics = dateTimeStatistics
                this.statistics = statistics
                this.paletteType = paletteType
                this.displayDateTime = displayDateTime.unixMillisLong
            }
        }
    }
}